import React, { useEffect, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { PulseLoader } from "react-spinners";
import { useLocation } from "react-router-dom";
import { IEmail } from "../../../../interfaces/IEmailAlerts";
import useMappings from "./Mappings";
import "./AddEmails.css";

interface AddEmailsProps {
  email: IEmail | null;
}

const AddEmails: React.FC<AddEmailsProps> = ({ email }) => {
  const { tasks, files, isLoading, isSaving, saveEmail, getFiles, updateEmail } = useMappings();
  const location = useLocation();
  const emailData = email || location.state?.email; // Prioritize prop over URL state
  const [emailAddress, setEmailAddress] = useState(emailData?.Address || "");
  const [selectedTask, setSelectedTask] = useState<string>(emailData?.Task || "");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  
  useEffect(() => {
    if (emailData) {
      setEmailAddress(emailData.Address || "");
      setSelectedTask(emailData.Task || "");
      // Ensure that files are fetched only after setting the task
      if (emailData.Files && emailData.Files.length > 0) {
        const fileNames = emailData.Files.map((file: any) => Object.values(file)[0]);
        setSelectedFiles(fileNames);
      }
      // Fetch files only if a task is selected
      if (emailData.Task) {
        getFiles(emailData.Task);
      }
    } else {
      setEmailAddress("");
      setSelectedTask("");
      setSelectedFiles([]);
    }
  }, [emailData]);

  const handleSubmit = () => {
    if (emailData) {
      // Call updateEmail when editing an existing email
      updateEmail(emailAddress, selectedTask, selectedFiles)
        .then((result) => {
          if (result) {
            setEmailAddress("");
            setSelectedTask("");
            setSelectedFiles([]);
          }
        })
        .catch(() => {});
    } else {
      // Call saveEmail when adding a new email
      saveEmail(emailAddress, selectedTask, selectedFiles)
        .then((result) => {
          if (result) {
            setEmailAddress("");
            setSelectedTask("");
            setSelectedFiles([]);
          }
        })
        .catch(() => {});
    }
  };
 
  const handleTaskChange = (event: any) => {
    setSelectedFiles([]);
    setSelectedTask(event.target.value);
    getFiles(event.target.value); 
  };

  return (
    <div className="add-email-container">
      <select
        value={selectedTask}
        onChange={handleTaskChange}
        className="dropdown md:w-20rem"
        disabled={isLoading || isSaving || emailData}
      >
        <option value="">Select Tasks</option>
        {tasks?.Task.map((p, index) => (
          <option key={index} value={p}>
            {p}
          </option>
        ))}
      </select>

      <MultiSelect
        value={selectedFiles}
        onChange={(e) => setSelectedFiles(e.value)}
        options={files?.map((f) => ({
          name: f,   
          value: f  
        })) || []}
        optionLabel="name" 
        placeholder="Select Files"
        className="dropdown md:w-20rem"
        disabled={!selectedTask}
      />

      <input
        type="text"
        className="input"
        placeholder="Enter Email Address"
        value={emailAddress}
        onChange={(e) => setEmailAddress(e.target.value)}
        disabled={isLoading || isSaving || !selectedTask || !selectedFiles}
      />

      <button 
        className="button" 
        onClick={handleSubmit} 
        disabled={isLoading || isSaving || !selectedTask || !selectedFiles}
      >
        {!isSaving ? (emailData ? "Update Email" : "Submit") : <PulseLoader color="white" size={10} />}
      </button>
    </div>
  );
};

export default AddEmails;
